import {THREE} from '../../base/three-defs.js';

// Renders the three LUTs of the Hillaire 2020 sky model (see
// shaders/atmosphere/atmosphere-common.glsl for the model itself).
// Transmittance and multiple-scattering depend only on the medium, so they
// are rendered once at init; the sky-view LUT is re-rendered whenever the
// sun moves or the camera changes altitude - at 192x108 it costs less than
// a single cloud raymarch step per frame.

const TRANSMITTANCE_WIDTH = 256;
const TRANSMITTANCE_HEIGHT = 64;
const MULTISCATTER_RES = 32;
// Must match ATMO_SKYVIEW_RES in atmosphere-common.glsl.
const SKYVIEW_WIDTH = 192;
const SKYVIEW_HEIGHT = 108;

// CPU mirror of the medium in atmosphere-common.glsl, for values the JS
// side needs (directional light colour). Keep in sync with the GLSL.
const GROUND_RADIUS = 6360.0;
const TOP_RADIUS = 6460.0;
const RAYLEIGH_SCATTER = [0.005802, 0.013558, 0.033100];
const MIE_SCATTER = 0.003996;
const MIE_ABSORB = 0.004440;
const OZONE_ABSORB = [0.000650, 0.001881, 0.000085];

// Transmittance toward the sun from ground level, same integral as the
// transmittance LUT (40 steps). ~40 iterations of arithmetic - fine to
// call once per frame. Writes into `out` ([r, g, b]) and returns it.
export function SunTransmittanceCPU(sinElev, out) {
  const mu = Math.max(-1, Math.min(1, sinElev));
  const r = GROUND_RADIUS + 0.002;
  // Distance to the top of the atmosphere along (mu, sqrt(1-mu^2)).
  const b = r * mu;
  const disc = b * b - (r * r - TOP_RADIUS * TOP_RADIUS);
  const tEnd = -b + Math.sqrt(Math.max(disc, 0));

  const STEPS = 40;
  const dt = tEnd / STEPS;
  const sinZen = Math.sqrt(Math.max(1 - mu * mu, 0));
  let odR = 0, odG = 0, odB = 0;
  for (let i = 0; i < STEPS; ++i) {
    const t = (i + 0.5) * dt;
    const px = sinZen * t;
    const py = r + mu * t;
    const h = Math.sqrt(px * px + py * py) - GROUND_RADIUS;

    const densityR = Math.exp(-h / 8.0);
    const densityM = Math.exp(-h / 1.2);
    const densityO = Math.max(0, 1 - Math.abs(h - 25.0) / 15.0);
    const mieExt = (MIE_SCATTER + MIE_ABSORB) * densityM;

    odR += (RAYLEIGH_SCATTER[0] * densityR + mieExt + OZONE_ABSORB[0] * densityO) * dt;
    odG += (RAYLEIGH_SCATTER[1] * densityR + mieExt + OZONE_ABSORB[1] * densityO) * dt;
    odB += (RAYLEIGH_SCATTER[2] * densityR + mieExt + OZONE_ABSORB[2] * densityO) * dt;
  }

  out[0] = Math.exp(-odR);
  out[1] = Math.exp(-odG);
  out[2] = Math.exp(-odB);
  return out;
}

function makeTarget(w, h) {
  const rt = new THREE.WebGLRenderTarget(w, h, {
    type: THREE.HalfFloatType,
    magFilter: THREE.LinearFilter,
    minFilter: THREE.LinearFilter,
    wrapS: THREE.ClampToEdgeWrapping,
    wrapT: THREE.ClampToEdgeWrapping,
    generateMipmaps: false,
    depthBuffer: false,
  });
  rt.texture.name = 'atmosphere-lut';
  return rt;
}

export class AtmosphereLUTs {
  #transmittanceRT_;
  #multiScatterRT_;
  #skyViewRT_;
  #skyViewMaterial_;
  #scene_;
  #quad_;
  #camera_;
  #ready_;

  constructor() {
    this.#transmittanceRT_ = makeTarget(TRANSMITTANCE_WIDTH, TRANSMITTANCE_HEIGHT);
    this.#multiScatterRT_ = makeTarget(MULTISCATTER_RES, MULTISCATTER_RES);
    this.#skyViewRT_ = makeTarget(SKYVIEW_WIDTH, SKYVIEW_HEIGHT);
    this.#skyViewMaterial_ = null;
    this.#scene_ = new THREE.Scene();
    this.#camera_ = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1);
    this.#quad_ = new THREE.Mesh(new THREE.PlaneGeometry(2, 2), null);
    this.#quad_.frustumCulled = false;
    this.#scene_.add(this.#quad_);
    this.#ready_ = false;
  }

  get transmittanceTexture() { return this.#transmittanceRT_.texture; }
  get multiScatterTexture() { return this.#multiScatterRT_.texture; }
  get skyViewTexture() { return this.#skyViewRT_.texture; }
  get ready() { return this.#ready_; }

  async init(renderer) {
    const loadText = async (url) => {
      const d = await fetch(url);
      return await d.text();
    };

    // Standalone include chain - the game's global shader bundle is not
    // used here, only the shared helpers the atmosphere file leans on.
    const common = await loadText('shaders/common.glsl');
    const atmoCommon = await loadText('shaders/atmosphere/atmosphere-common.glsl');
    const vsh = await loadText('shaders/atmosphere/fullscreen-vsh.glsl');
    const transmittanceFsh = await loadText('shaders/atmosphere/transmittance-lut-fsh.glsl');
    const multiScatterFsh = await loadText('shaders/atmosphere/multiscatter-lut-fsh.glsl');
    const skyViewFsh = await loadText('shaders/atmosphere/skyview-lut-fsh.glsl');

    const prelude = common + '\n' + atmoCommon + '\n';

    const renderPass = (material, target) => {
      this.#quad_.material = material;
      const prev = renderer.getRenderTarget();
      renderer.setRenderTarget(target);
      renderer.render(this.#scene_, this.#camera_);
      renderer.setRenderTarget(prev);
    };

    const transmittanceMaterial = new THREE.ShaderMaterial({
      vertexShader: vsh,
      fragmentShader: prelude + transmittanceFsh,
      depthTest: false,
      depthWrite: false,
    });
    renderPass(transmittanceMaterial, this.#transmittanceRT_);
    transmittanceMaterial.dispose();

    const multiScatterMaterial = new THREE.ShaderMaterial({
      uniforms: {
        uAtmoTransmittanceLUT: {value: this.#transmittanceRT_.texture},
      },
      vertexShader: vsh,
      fragmentShader: prelude + multiScatterFsh,
      depthTest: false,
      depthWrite: false,
    });
    renderPass(multiScatterMaterial, this.#multiScatterRT_);
    multiScatterMaterial.dispose();

    this.#skyViewMaterial_ = new THREE.ShaderMaterial({
      uniforms: {
        uAtmoTransmittanceLUT: {value: this.#transmittanceRT_.texture},
        uAtmoMultiScatterLUT: {value: this.#multiScatterRT_.texture},
        uSunZenithCos: {value: -1.0},
        uViewHeight: {value: 6360.2},
      },
      vertexShader: vsh,
      fragmentShader: prelude + skyViewFsh,
      depthTest: false,
      depthWrite: false,
    });

    this.#ready_ = true;
  }

  // sunZenithCos: sun elevation cos at the camera; viewHeightKm: distance
  // from planet centre in km.
  renderSkyView(renderer, sunZenithCos, viewHeightKm) {
    if (!this.#ready_) {
      return;
    }
    this.#skyViewMaterial_.uniforms.uSunZenithCos.value = sunZenithCos;
    this.#skyViewMaterial_.uniforms.uViewHeight.value = viewHeightKm;

    this.#quad_.material = this.#skyViewMaterial_;
    const prev = renderer.getRenderTarget();
    renderer.setRenderTarget(this.#skyViewRT_);
    renderer.render(this.#scene_, this.#camera_);
    renderer.setRenderTarget(prev);
  }
}
